import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { ReactComponent as LogoVoopter } from "assets/svgs/logo-voopter.svg";
import airports from "shared/config/airports/AllAirports/airports.json";
import { airportPros } from "shared/config/airports/AllAirports/types";
import { sendTrackingWebhook } from "shared/services/trackingWebhook/send-tracking-webhook.service";

const REDIRECT_MIN_DELAY_MS = 4000;
const GTM_EVENT_TIMEOUT_MS = 2800;

const getCachedLocalStorageItem = (key: string) => {
  const storageWithCache = localStorage as Storage & {
    getCacheItem?: (key: string) => string | null;
  };

  return storageWithCache.getCacheItem?.(key) ?? localStorage.getItem(key);
};

const getTripDuration = (leaveDate: string, returnDate: string) => {
  if (!leaveDate || !returnDate) {
    return undefined;
  }

  const departure = new Date(
    Number(leaveDate.slice(0, 4)),
    Number(leaveDate.slice(4, 6)) - 1,
    Number(leaveDate.slice(6, 8)),
  );
  const returning = new Date(
    Number(returnDate.slice(0, 4)),
    Number(returnDate.slice(4, 6)) - 1,
    Number(returnDate.slice(6, 8)),
  );
  const differenceInMs = returning.getTime() - departure.getTime();

  if (Number.isNaN(differenceInMs)) {
    return undefined;
  }

  return Math.floor(differenceInMs / 86400000) + 1;
};

const getUserContactId = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") ?? "null") as {
      contactID?: string;
    } | null;

    return user?.contactID ?? "";
  } catch {
    return "";
  }
};

const findAirportByCode = (airportCode: string) =>
  (airports as airportPros[]).find(
    (airport) => airport.airport_code === airportCode,
  );

type LegacyRedirectIdParams = {
  airline: string;
  webservice: string;
  seller: string;
  origin: string;
  destination: string;
  leaveDate: string;
  returnDate: string;
  price: string;
  roundTrip: string;
  adults: string;
  children: string;
  babies: string;
  isInternationalFlight: string;
  originCountryCode: string;
  destinationCountryCode: string;
  ip: string;
  user: string;
};

const getCurrentTime = () => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");

  return `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
    now.getSeconds(),
  )}`;
};

const buildLegacyRedirectId = ({
  airline,
  webservice,
  seller,
  origin,
  destination,
  leaveDate,
  returnDate,
  price,
  roundTrip,
  adults,
  children,
  babies,
  isInternationalFlight,
  originCountryCode,
  destinationCountryCode,
  ip,
  user,
}: LegacyRedirectIdParams) =>
  [
    `al=${airline}`,
    `ws=${webservice}`,
    `se=${seller}`,
    `ao=${origin}`,
    `ad=${destination}`,
    `dl=${leaveDate}`,
    `dr=${returnDate}`,
    `pr=${price}`,
    `rt=${roundTrip}`,
    `a=${adults}`,
    `b=${children}`,
    `c=${babies}`,
    `et=${getCurrentTime()}`,
    `ip=${ip}`,
    `us=${user}`,
    `if=${isInternationalFlight}`,
    `co=${originCountryCode}`,
    `cd=${destinationCountryCode}`,
  ].join("|");

export default function Redirect() {
  const { search } = useLocation();
  const hasRedirectedRef = useRef(false);
  const [partnerLogoFailed, setPartnerLogoFailed] = useState(false);

  const redirectData = useMemo(() => {
    const params = new URLSearchParams(search);
    const targetUrl = params.get("url");
    const sellerName =
      params.get("sale[data4]") || params.get("domain") || "site parceiro";
    const webservice = params.get("sale[data2]")?.toLowerCase() ?? "";

    return {
      targetUrl,
      sellerName,
      partnerLogoUrl:
        params.get("sale[partnerLogo]") || params.get("partnerLogo"),
      isSkyscanner: webservice === "sk" || webservice === "skyscanner",
    };
  }, [search]);

  useEffect(() => {
    setPartnerLogoFailed(false);
  }, [redirectData.partnerLogoUrl]);

  useEffect(() => {
    if (!redirectData.targetUrl) {
      return;
    }

    let isMinimumDelayFinished = false;
    let isGtmFinished = false;

    const redirectToPartner = () => {
      if (
        !isMinimumDelayFinished ||
        !isGtmFinished ||
        hasRedirectedRef.current
      ) {
        return;
      }

      hasRedirectedRef.current = true;
      window.location.replace(redirectData.targetUrl as string);
    };

    const minimumDelayTimer = window.setTimeout(() => {
      isMinimumDelayFinished = true;
      redirectToPartner();
    }, REDIRECT_MIN_DELAY_MS);

    const gtmFallbackTimer = window.setTimeout(() => {
      isGtmFinished = true;
      redirectToPartner();
    }, GTM_EVENT_TIMEOUT_MS);

    const params = new URLSearchParams(search);
    const totalCost = params.get("sale[totalCost]") ?? "";
    const price = totalCost.slice(3);
    const origin = params.get("sale[origin]") ?? "";
    const destination = params.get("sale[destination]") ?? "";
    const leaveDate = params.get("sale[leaveDate]") ?? "";
    const returnDate = params.get("sale[returnDate]") ?? "";
    const redirectId = params.get("sale[redirectId]") ?? "";
    const userContactId = getUserContactId();
    const webservice = params.get("sale[data2]") ?? "";
    const airline = params.get("sale[data3]") ?? "";
    const seller = params.get("sale[data4]") || params.get("domain") || "";
    const adults = params.get("sale[nAdults]") ?? "";
    const children = params.get("sale[nChildren]") ?? "";
    const babies = params.get("sale[nBabies]") ?? "";
    const originAirport = findAirportByCode(origin);
    const destinationAirport = findAirportByCode(destination);
    const originCountryCode = originAirport?.country_code ?? "";
    const destinationCountryCode = destinationAirport?.country_code ?? "";
    const isInternationalFlight =
      originCountryCode && destinationCountryCode
        ? originCountryCode === "BR" && destinationCountryCode === "BR"
          ? "0"
          : "1"
        : "";
    const legacyRedirectId =
      redirectId ||
      buildLegacyRedirectId({
        airline,
        webservice,
        seller,
        origin,
        destination,
        leaveDate,
        returnDate,
        price,
        roundTrip: params.get("sale[rt]") ?? "0",
        adults,
        children,
        babies,
        isInternationalFlight,
        originCountryCode,
        destinationCountryCode,
        ip: params.get("sale[ip]") ?? "",
        user: params.get("sale[user]") ?? "",
      });

    const dataToGoogleTagManager: Record<string, string | number | null> = {
      sale_source: getCachedLocalStorageItem("sale_source"),
      sale_subsource: getCachedLocalStorageItem("sale_subsource"),
      sale_userid: getCachedLocalStorageItem("sale_userid"),
      // Hash do e-mail que o app manda para o cruzamento da campanha AdvancedBR.
      user_key: params.get("sale[user_key]"),
      webservice,
      IATA_origin: origin,
      IATA_destination: destination,
      IATA_route: `${origin}-${destination}`,
      adults,
      children,
      babies,
      redirectID: `${legacyRedirectId}${userContactId}`,
      price,
      currency: totalCost.slice(0, 3),
      IATA_airline: airline,
      IATA_origin_autocomplete: originAirport?.city_name ?? "",
      IATA_destination_autocomplete: destinationAirport?.city_name ?? "",
      is_international_flight: isInternationalFlight,
      seller,
      country_origin: originCountryCode,
      country_destination: destinationCountryCode,
    };

    if (leaveDate) {
      dataToGoogleTagManager.departure_yyyy = leaveDate.slice(0, 4);
      dataToGoogleTagManager.departure_mm = leaveDate.slice(4, 6);
      dataToGoogleTagManager.departure_dd = leaveDate.slice(6, 8);
    }

    if (returnDate) {
      dataToGoogleTagManager.return_yyyy = returnDate.slice(0, 4);
      dataToGoogleTagManager.return_mm = returnDate.slice(4, 6);
      dataToGoogleTagManager.return_dd = returnDate.slice(6, 8);
    }

    const tripDuration = getTripDuration(leaveDate, returnDate);

    if (tripDuration) {
      dataToGoogleTagManager.trip_duration = tripDuration;
    }

    const flightRedirectEvent = {
      event: "flightRedirect",
      eventCallback: () => {
        window.clearTimeout(gtmFallbackTimer);
        isGtmFinished = true;
        redirectToPartner();
      },
      eventTimeout: GTM_EVENT_TIMEOUT_MS,
    };

    // console.log(
    //   "[Redirect GTM dataLayer]",
    //   JSON.stringify([dataToGoogleTagManager, flightRedirectEvent], null, 2),
    // );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(dataToGoogleTagManager);
    sendTrackingWebhook("redirect", dataToGoogleTagManager);
    window.dataLayer.push(flightRedirectEvent);

    return () => {
      window.clearTimeout(minimumDelayTimer);
      window.clearTimeout(gtmFallbackTimer);
    };
  }, [redirectData.targetUrl, search]);

  return (
    <CleanPage>
      <CleanTopLogo aria-label="Voopter">
        <LogoVoopter />
      </CleanTopLogo>

      <CleanContent aria-live="polite">
        <CleanMessage>Estamos levando você até</CleanMessage>
        <CleanPartnerLogo aria-label={redirectData.sellerName}>
          {redirectData.partnerLogoUrl && !partnerLogoFailed ? (
            <img
              src={redirectData.partnerLogoUrl}
              alt={redirectData.sellerName}
              onError={() => setPartnerLogoFailed(true)}
            />
          ) : (
            <CleanPartnerWordmark>
              {redirectData.sellerName}
            </CleanPartnerWordmark>
          )}
        </CleanPartnerLogo>

        <CleanProgressTrack
          role="progressbar"
          aria-label="Redirecionamento em andamento"
          aria-valuetext="Carregando"
        >
          <CleanProgressFill />
        </CleanProgressTrack>

        {redirectData.targetUrl ? (
          <CleanFallbackLink href={redirectData.targetUrl}>
            Se o redirecionamento não acontecer, clique aqui.
          </CleanFallbackLink>
        ) : (
          <CleanMissingUrl>Link do parceiro indisponível.</CleanMissingUrl>
        )}
      </CleanContent>

      <CleanAttribution>
        <CleanAttributionText>com tecnologia do</CleanAttributionText>
        {redirectData.isSkyscanner ? (
          <CleanSkyscannerLogo aria-label="Skyscanner">
            <CleanSkyscannerMark viewBox="0 0 30 22" aria-hidden="true">
              <path d="M3 18h24M6 14a9 9 0 0 1 18 0M15 1v5M4.5 6.5l3.6 3M25.5 6.5l-3.6 3" />
            </CleanSkyscannerMark>
            <span>Skyscanner</span>
          </CleanSkyscannerLogo>
        ) : (
          <CleanFooterVoopter aria-label="Voopter">
            <LogoVoopter />
          </CleanFooterVoopter>
        )}
      </CleanAttribution>
    </CleanPage>
  );
}

const pulse = keyframes`
  0%, 100% {
    opacity: 0.38;
    transform: translateX(0) scale(0.96);
  }

  50% {
    opacity: 1;
    transform: translateX(4px) scale(1);
  }
`;

const fill = keyframes`
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(100%);
  }
`;

const Page = styled.main`
  width: 100%;
  height: 100vh;
  height: 100dvh;
  min-height: 100vh;
  min-height: 100dvh;
  display: grid;
  place-items: center;
  padding: 40px 20px;
  box-sizing: border-box;
  overflow: hidden;
  color: #17415a;
  background:
    linear-gradient(
      135deg,
      rgba(235, 250, 255, 0.96),
      rgba(255, 255, 255, 0.9)
    ),
    radial-gradient(
      circle at 18% 24%,
      rgba(249, 203, 23, 0.18),
      transparent 28%
    ),
    radial-gradient(
      circle at 82% 68%,
      rgba(105, 216, 148, 0.2),
      transparent 30%
    ),
    #f6fbfd;
  font-family:
    Nunito,
    Inter,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;

  @media (max-width: 720px) {
    padding: clamp(14px, 3.5dvh, 24px) 16px;
  }
`;

const Content = styled.section`
  width: min(920px, 100%);
  display: grid;
  gap: 56px;
  text-align: center;

  @media (max-width: 720px) {
    max-height: 100%;
    gap: clamp(16px, 3dvh, 26px);
  }
`;

const Header = styled.header`
  display: grid;
  gap: 12px;

  @media (max-width: 720px) {
    gap: 8px;
  }
`;

const Eyebrow = styled.p`
  margin: 0;
  color: #2789b3;
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;

  @media (max-width: 720px) {
    font-size: clamp(0.62rem, 2.4vw, 0.72rem);
  }
`;

const Title = styled.h1`
  margin: 0;
  color: #17415a;
  font-size: clamp(2.4rem, 3vw, 4.5rem);
  font-weight: 800;
  line-height: 1;

  @media (max-width: 720px) {
    font-size: clamp(1.9rem, 9vw, 2.6rem);
  }
`;

const Subtitle = styled.p`
  max-width: 620px;
  margin: 0 auto;
  color: #536674;
  font-size: clamp(1rem, 1vw, 1.35rem);
  line-height: 1.45;

  @media (max-width: 720px) {
    max-width: 330px;
    font-size: clamp(0.82rem, 3.6vw, 0.96rem);
    line-height: 1.28;
  }
`;

const SellerName = styled.strong`
  color: #219653;
  font-weight: 900;
  text-transform: uppercase;
`;

const TransferArea = styled.div`
  display: grid;
  grid-template-columns: 190px minmax(220px, 1fr) 190px;
  align-items: center;
  gap: 34px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: clamp(10px, 2.4dvh, 16px);
  }

  @media (max-width: 720px) and (max-height: 700px) {
    width: min(360px, 100%);
    margin: 0 auto;
    grid-template-columns:
      clamp(86px, 28vw, 112px)
      minmax(0, 1fr)
      clamp(86px, 28vw, 112px);
    gap: clamp(6px, 2vw, 10px);
  }
`;

const BrandCircle = styled.div`
  height: 190px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(207, 239, 250, 0.95);
  box-shadow: 0 24px 60px rgba(23, 65, 90, 0.14);

  svg {
    width: 132px;
    height: auto;
  }

  @media (max-width: 720px) {
    width: clamp(106px, 34vw, 138px);
    height: clamp(106px, 34vw, 138px);
    margin: 0 auto;

    svg {
      width: 82%;
      max-width: 108px;
    }
  }

  @media (max-width: 720px) and (max-height: 700px) {
    width: clamp(86px, 28vw, 112px);
    height: clamp(86px, 28vw, 112px);
  }
`;

const RouteMiddle = styled.div`
  display: grid;
  justify-items: center;
  gap: 16px;
  min-width: 0;

  @media (max-width: 720px) {
    order: 2;
    gap: clamp(8px, 1.8dvh, 12px);
  }

  @media (max-width: 720px) and (max-height: 700px) {
    order: initial;
  }
`;

const RouteText = styled.p`
  margin: 0;
  color: #536674;
  font-size: 1.18rem;
  font-weight: 700;

  @media (max-width: 720px) {
    font-size: clamp(0.82rem, 3.4vw, 0.95rem);
  }

  @media (max-width: 720px) and (max-height: 700px) {
    font-size: clamp(0.68rem, 3vw, 0.8rem);
    line-height: 1.15;
  }
`;

const ProgressTrack = styled.div`
  width: min(260px, 100%);
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(39, 137, 179, 0.14);

  @media (max-width: 720px) {
    width: min(220px, 72vw);
    height: 6px;
  }

  @media (max-width: 720px) and (max-height: 700px) {
    width: 100%;
  }
`;

const ProgressFill = styled.div`
  width: 70%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #00daff, #2789b3, #69d894);
  animation: ${fill} 1.35s ease-in-out infinite;
`;

const ChevronRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;

  @media (max-width: 720px) {
    gap: 8px;
    margin-top: 0;
  }

  @media (max-width: 720px) and (max-height: 700px) {
    gap: clamp(4px, 1.5vw, 7px);
  }
`;

const Chevron = styled.span<{ $delay: string }>`
  position: relative;
  width: 32px;
  height: 40px;
  animation: ${pulse} 1s ease-in-out infinite;
  animation-delay: ${({ $delay }) => $delay};

  @media (max-width: 720px) {
    width: 24px;
    height: 30px;
  }

  @media (max-width: 720px) and (max-height: 700px) {
    width: clamp(16px, 5vw, 20px);
    height: 26px;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    right: 0;
    top: 50%;
    width: 34px;
    height: 4px;
    border-radius: 999px;
    background: #35bde8;
    transform-origin: right center;

    @media (max-width: 720px) {
      width: 26px;
      height: 3px;
    }

    @media (max-width: 720px) and (max-height: 700px) {
      width: clamp(18px, 5.5vw, 22px);
    }
  }

  &::before {
    transform: translateY(-55%) rotate(45deg);
  }

  &::after {
    transform: translateY(-55%) rotate(-45deg);
  }
`;

const PartnerCircle = styled.div`
  height: 190px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  padding: 20px;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid rgba(105, 216, 148, 0.36);
  box-shadow: 0 24px 60px rgba(33, 150, 83, 0.16);

  @media (max-width: 720px) {
    order: 3;
    width: clamp(106px, 34vw, 138px);
    height: clamp(106px, 34vw, 138px);
    margin: 0 auto;
    gap: 6px;
    padding: 14px;
  }

  @media (max-width: 720px) and (max-height: 700px) {
    order: initial;
    width: clamp(86px, 28vw, 112px);
    height: clamp(86px, 28vw, 112px);
  }
`;

const PartnerInitial = styled.span`
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  color: #ffffff;
  background: linear-gradient(135deg, #219653, #69d894);
  font-size: 1.35rem;
  font-weight: 900;
  text-transform: uppercase;

  @media (max-width: 720px) {
    width: 34px;
    height: 34px;
    font-size: 1rem;
  }

  @media (max-width: 720px) and (max-height: 700px) {
    width: 28px;
    height: 28px;
    font-size: 0.88rem;
  }
`;

const PartnerName = styled.strong`
  max-width: 138px;
  color: #219653;
  font-size: 1.05rem;
  font-weight: 900;
  line-height: 1.2;
  overflow-wrap: anywhere;
  text-transform: uppercase;

  @media (max-width: 720px) {
    max-width: 108px;
    font-size: clamp(0.76rem, 3vw, 0.88rem);
  }

  @media (max-width: 720px) and (max-height: 700px) {
    max-width: 80px;
    font-size: 0.68rem;
  }
`;

const Footer = styled.footer`
  display: grid;
  gap: 10px;
  justify-items: center;
  color: #536674;
  font-size: 0.98rem;

  @media (max-width: 720px) {
    gap: 6px;
    font-size: clamp(0.76rem, 3vw, 0.88rem);
  }
`;

const IncludedFees = styled.p`
  margin: 0;
`;

const FallbackLink = styled.a`
  color: #1d5a80;
  font-weight: 800;
  text-decoration: underline;
  text-underline-offset: 4px;

  &:hover {
    color: #219653;
  }
`;

const MissingUrl = styled.p`
  margin: 0;
  color: #ff5d50;
  font-weight: 800;
`;

const cleanFill = keyframes`
  0% { width: 8%; }
  65% { width: 76%; }
  100% { width: 94%; }
`;

const CleanPage = styled.main`
  position: relative;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  padding: clamp(32px, 5vh, 56px) 24px;
  box-sizing: border-box;
  overflow: hidden;
  color: #071d49;
  background: #ffffff;
  font-family:
    Nunito,
    Inter,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    sans-serif;
`;

const CleanTopLogo = styled.div`
  display: flex;
  justify-content: center;

  svg {
    width: clamp(126px, 11vw, 164px);
    height: auto;
  }
`;

const CleanContent = styled.section`
  position: absolute;
  top: 50%;
  left: 50%;
  width: min(400px, calc(100% - 48px));
  transform: translate(-50%, -50%);
  display: grid;
  justify-items: center;
  text-align: center;

  @media (max-height: 560px) {
    top: 48%;
  }
`;

const CleanMessage = styled.h1`
  margin: 0;
  font-size: clamp(17px, 2vw, 20px);
  font-weight: 600;
  line-height: 1.35;
`;

const CleanPartnerLogo = styled.div`
  width: 100%;
  min-height: 58px;
  margin: 10px 0 20px;
  display: grid;
  place-items: center;

  img {
    display: block;
    width: auto;
    max-width: min(220px, 75%);
    height: auto;
    max-height: 58px;
    object-fit: contain;
  }
`;

const CleanPartnerWordmark = styled.strong`
  max-width: 100%;
  color: #111827;
  font-size: clamp(25px, 5vw, 32px);
  font-weight: 900;
  line-height: 1;
  overflow-wrap: anywhere;
`;

const CleanProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: #edf1f7;
`;

const CleanProgressFill = styled.div`
  width: 8%;
  height: 100%;
  border-radius: inherit;
  background: #0867df;
  animation: ${cleanFill} ${REDIRECT_MIN_DELAY_MS}ms ease-out forwards;

  @media (prefers-reduced-motion: reduce) {
    width: 94%;
    animation: none;
  }
`;

const CleanFallbackLink = styled.a`
  margin-top: 15px;
  color: #54657e;
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    color: #0867df;
    text-decoration: underline;
  }
`;

const CleanMissingUrl = styled.p`
  margin: 15px 0 0;
  color: #c93030;
  font-size: 13px;
  font-weight: 700;
`;

const CleanAttribution = styled.footer`
  position: absolute;
  bottom: clamp(28px, 7vh, 68px);
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  white-space: nowrap;
`;

const CleanAttributionText = styled.span`
  color: #202938;
  font-size: 12px;
`;

const CleanSkyscannerLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #0770e3;
  font-size: 18px;
  font-weight: 800;
`;

const CleanSkyscannerMark = styled.svg`
  width: 27px;
  height: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.7;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const CleanFooterVoopter = styled.div`
  display: flex;

  svg {
    width: 88px;
    height: auto;
  }
`;
